/**
 * ShortageRegisterTab — Audit Center → Shortages (BT Batch 2 P1).
 * Per-branch at-a-glance of every transfer shortage and how it ended:
 * actual loss vs false positive vs still under investigation.
 * A shortage books NO loss until its discrepancy ticket gets a verdict —
 * this register is where the owner checks nothing is rotting unresolved.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, ExternalLink, AlertTriangle, CheckCircle2, SearchCheck } from 'lucide-react';
import { fmtDateTime, formatPHP } from '../../lib/utils';

const BUCKET_META = {
  under_investigation: { label: 'Under Investigation', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: SearchCheck },
  actual_loss:         { label: 'Actual Loss',         cls: 'bg-rose-100 text-rose-700 border-rose-300', icon: AlertTriangle },
  false_positive:      { label: 'False Positive',      cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
};

const RESOLUTION_LABELS = {
  transit_loss: 'Transit Loss', sender_error: 'Never Loaded / Miscount',
  receiver_error: 'Receiver Miscount', write_off: 'Written Off',
  insurance_claim: 'Insurance Claim', partial_recovery: 'Partial Recovery',
  shrinkage: 'Shrinkage',
};

export default function ShortageRegisterTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState('all');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/incident-tickets/shortage-register', { params });
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter(r =>
    (branchFilter === 'all' || r.from_branch_id === branchFilter) &&
    (bucketFilter === 'all' || r.bucket === bucketFilter));
  const totals = data?.totals || {};
  const branches = data?.branches || [];

  return (
    <div className="space-y-4" data-testid="shortage-register-tab">
      {/* ── Totals strip ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="border-amber-200">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-amber-700 font-semibold">Under Investigation</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="sr-open-count">{totals.under_investigation ?? 0}</div>
            <div className="text-xs text-slate-500">{formatPHP(totals.capital_at_stake || 0)} at stake — no loss booked yet</div>
          </CardContent>
        </Card>
        <Card className="border-rose-200">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-rose-700 font-semibold">Actual Losses</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="sr-loss-count">{totals.actual_loss ?? 0}</div>
            <div className="text-xs text-slate-500">{formatPHP(totals.booked_loss || 0)} confirmed &amp; in the books</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="p-4">
            <div className="text-xs uppercase text-emerald-700 font-semibold">False Positives</div>
            <div className="text-2xl font-bold text-slate-800" data-testid="sr-fp-count">{totals.false_positive ?? 0}</div>
            <div className="text-xs text-slate-500">{formatPHP(totals.cleared_capital || 0)} cleared — goods were never lost</div>
          </CardContent>
        </Card>
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Sending branch</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="sr-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map(b => (
                <SelectItem key={b.branch_id} value={b.branch_id}>{b.branch_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Outcome</Label>
          <Select value={bucketFilter} onValueChange={setBucketFilter}>
            <SelectTrigger className="h-9 w-48" data-testid="sr-bucket-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outcomes</SelectItem>
              <SelectItem value="under_investigation">Under investigation</SelectItem>
              <SelectItem value="actual_loss">Actual loss</SelectItem>
              <SelectItem value="false_positive">False positive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="sr-date-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" data-testid="sr-date-to" />
        </div>
        <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="sr-refresh">
          <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {/* ── Per-branch summary ── */}
      {branches.length > 1 && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase text-slate-500">Sending Branch</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">Open</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">At Stake</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">Losses</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">Booked</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">False +</TableHead>
                <TableHead className="text-xs uppercase text-slate-500 text-right">Cleared</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {branches.map(b => (
                  <TableRow key={b.branch_id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setBranchFilter(b.branch_id)}>
                    <TableCell className="text-sm font-medium">{b.branch_name}</TableCell>
                    <TableCell className="text-right text-sm">{b.under_investigation}</TableCell>
                    <TableCell className="text-right text-sm text-amber-700">{formatPHP(b.capital_at_stake)}</TableCell>
                    <TableCell className="text-right text-sm">{b.actual_loss}</TableCell>
                    <TableCell className="text-right text-sm text-rose-700">{formatPHP(b.booked_loss)}</TableCell>
                    <TableCell className="text-right text-sm">{b.false_positive}</TableCell>
                    <TableCell className="text-right text-sm text-emerald-700">{formatPHP(b.cleared_capital)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Ticket rows ── */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-xs uppercase text-slate-500">Ticket</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Transfer</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Route</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Outcome</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Verdict</TableHead>
              <TableHead className="text-xs uppercase text-slate-500 text-right">Shortage ₱</TableHead>
              <TableHead className="text-xs uppercase text-slate-500 text-right">Booked Loss</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Opened</TableHead>
              <TableHead className="text-xs uppercase text-slate-500">Resolved</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-slate-400 py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-sm text-slate-400 py-8">
                  No transfer shortages in this view — nothing pending, nothing lost.
                </TableCell></TableRow>
              ) : rows.map(r => {
                const meta = BUCKET_META[r.bucket] || BUCKET_META.under_investigation;
                return (
                  <TableRow key={r.ticket_id} data-testid={`sr-row-${r.ticket_number}`}>
                    <TableCell>
                      <button
                        onClick={() => window.open(`/incident-tickets?ticket=${r.ticket_number}`, '_blank')}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-mono text-xs flex items-center gap-1"
                        data-testid={`sr-open-${r.ticket_number}`}>
                        {r.ticket_number}<ExternalLink size={11} />
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.order_number}</TableCell>
                    <TableCell className="text-xs">{r.from_branch_name} → {r.to_branch_name}</TableCell>
                    <TableCell><Badge className={`text-[10px] border ${meta.cls}`}>{meta.label}</Badge></TableCell>
                    <TableCell className="text-xs">{RESOLUTION_LABELS[r.resolution_type] || (r.bucket === 'under_investigation' ? '— pending —' : r.resolution_type)}</TableCell>
                    <TableCell className="text-right text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(r.shortage_capital)}</TableCell>
                    <TableCell className={`text-right text-xs ${r.booked_loss > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.booked_loss > 0 ? formatPHP(r.booked_loss) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDateTime(r.created_at)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.resolved_at ? `${fmtDateTime(r.resolved_at)} · ${r.resolved_by_name}` : '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {data?.truncated && (
        <div className="text-xs text-amber-600">Showing the most recent 1,000 tickets — narrow the date range for older history.</div>
      )}
    </div>
  );
}
